/**
 * @fileoverview Contexto global de la sesión del participante.
 * Mantiene en memoria todos los datos del flujo de votación.
 *
 * El session_token y el código se persisten en sessionStorage para
 * sobrevivir a una recarga; el resto se reconstruye desde el backend.
 */

import { createContext, useContext, useEffect, useMemo, useReducer } from "react";

import * as api from "../lib/api";
import { guardarSesion, leerSesion, limpiarSesion } from "../lib/session";

/**
 * @typedef {Object} ParticipacionState
 * @property {string} codigo
 * @property {string} forum_id
 * @property {string} alcaldia_foro
 * @property {string} session_token
 * @property {string} participacion_id
 * @property {string} expira_en
 * @property {{alcaldia_residencia: string|null, edad_rango: string|null, genero: string|null, grupo: string|null}} datos
 * @property {import("../lib/schemas").Brecha[]} brechas
 * @property {Record<string,"acuerdo"|"desacuerdo"|"pasar">} votos
 * @property {number} indice_actual
 * @property {boolean} cargando_brechas
 * @property {string|null} error
 */

/** @type {ParticipacionState} */
const ESTADO_INICIAL = {
    codigo: "",
    forum_id: "",
    alcaldia_foro: "",
    session_token: "",
    participacion_id: "",
    expira_en: "",
    datos: {
        alcaldia_residencia: null,
        edad_rango: null,
        genero: null,
        grupo: null,
    },
    brechas: [],
    votos: {},
    indice_actual: 0,
    cargando_brechas: false,
    error: null,
};

/**
 * Inicializador lazy del reducer: hidrata desde sessionStorage en el
 * primer render para que cualquier ruta protegida tenga ya el
 * session_token al evaluar su guard. Sin esto, useEffect corre tarde
 * y los guards redirigen a / antes de la hidratación.
 * @returns {ParticipacionState}
 */
function hydrateInicial() {
    const persisted = leerSesion();
    if (!persisted) return ESTADO_INICIAL;
    return {
        ...ESTADO_INICIAL,
        codigo: persisted.codigo,
        session_token: persisted.session_token,
        participacion_id: persisted.participacion_id || "",
        expira_en: persisted.expira_en,
        forum_id: persisted.forum_id || "",
        alcaldia_foro: persisted.alcaldia_foro || "",
        indice_actual: typeof persisted.indice_actual === "number" ? persisted.indice_actual : 0,
    };
}

/**
 * @param {ParticipacionState} state
 * @param {{type: string, payload?: any}} action
 * @returns {ParticipacionState}
 */
function reducer(state, action) {
    switch (action.type) {
        case "RESET":
            return ESTADO_INICIAL;
        case "SET_CODIGO":
            return { ...state, codigo: action.payload };
        case "SET_FORUM":
            return {
                ...state,
                forum_id: action.payload.forum_id,
                alcaldia_foro: action.payload.alcaldia_foro,
            };
        case "SET_DATOS":
            return { ...state, datos: { ...state.datos, ...action.payload } };
        case "SET_SESION":
            return {
                ...state,
                session_token: action.payload.session_token,
                participacion_id: action.payload.participacion_id,
                expira_en: action.payload.expira_en,
            };
        case "SET_BRECHAS":
            return { ...state, brechas: action.payload, cargando_brechas: false };
        case "BRECHAS_LOADING":
            return { ...state, cargando_brechas: true };
        case "SET_VOTOS_PREVIOS": {
            /** @type {Record<string,any>} */
            const next = {};
            for (const v of action.payload) {
                next[v.statement_id] = v.valor;
            }
            return { ...state, votos: next };
        }
        case "SET_VOTO":
            return {
                ...state,
                votos: { ...state.votos, [action.payload.statement_id]: action.payload.valor },
            };
        case "SET_INDICE":
            return { ...state, indice_actual: action.payload };
        case "SET_ERROR":
            return { ...state, error: action.payload };
        default:
            return state;
    }
}

/** @type {React.Context<{state: ParticipacionState, dispatch: React.Dispatch<any>, statementsPlanas: import("../lib/schemas").Statement[], brechaDeStatement: (idx: number) => import("../lib/schemas").Brecha | undefined, cargarBrechas: () => Promise<void>}>} */
const Ctx = createContext(/** @type {any} */ (null));

/**
 * @param {{ children: React.ReactNode }} props
 */
export function ParticipacionProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, undefined, hydrateInicial);

    // Persiste cambios relevantes de sesión.
    useEffect(() => {
        if (state.session_token && state.codigo && state.expira_en) {
            guardarSesion({
                session_token: state.session_token,
                codigo: state.codigo,
                expira_en: state.expira_en,
                participacion_id: state.participacion_id || undefined,
                forum_id: state.forum_id || undefined,
                alcaldia_foro: state.alcaldia_foro || undefined,
                indice_actual: state.indice_actual,
            });
        }
    }, [
        state.session_token,
        state.codigo,
        state.expira_en,
        state.participacion_id,
        state.forum_id,
        state.alcaldia_foro,
        state.indice_actual,
    ]);

    /**
     * Cargar brechas desde el backend (requiere session_token activo).
     * Al terminar, también carga votos previos y reposiciona el índice
     * al primer pendiente — pieza clave de la persistencia de avance
     * para que reanudar lleve siempre al punto correcto.
     */
    async function cargarBrechas() {
        if (state.brechas.length > 0) return;
        dispatch({ type: "BRECHAS_LOADING" });
        try {
            const brechas = await api.obtenerBrechas();
            dispatch({ type: "SET_BRECHAS", payload: brechas });

            // Carga votos previos por si el participante reanuda.
            let previos = [];
            try {
                previos = await api.obtenerVotosPropios();
                dispatch({ type: "SET_VOTOS_PREVIOS", payload: previos });
            } catch (e) {
                // Degradación elegante: si el endpoint falla (ej. timeout o
                // 401 transitorio) seguimos con votos vacíos; el participante
                // verá las preguntas como si arrancara desde cero, sin crash.
                console.error("[ParticipacionContext] obtenerVotosPropios falló:", e);
            }

            // Reposicionar al primer pendiente.
            // Construimos la lista plana en el mismo orden que el render.
            /** @type {string[]} */
            const idsPlanos = [];
            for (const b of brechas) {
                for (const s of b.statements) idsPlanos.push(s.id);
            }
            const votosSet = new Set(previos.map((v) => v.statement_id));
            let primerPendiente = idsPlanos.findIndex((id) => !votosSet.has(id));
            if (primerPendiente < 0) {
                // Todas respondidas: nos quedamos en la última para que
                // el participante pueda revisar y pasar a la siguiente pantalla.
                primerPendiente = Math.max(0, idsPlanos.length - 1);
            }

            // Respetamos el índice persistido si el participante estaba
            // más adelante (p.ej. saltó voluntariamente con "Siguiente").
            // El persistido solo gana si apunta a una pregunta sin voto.
            const idxPersistido = state.indice_actual;
            const idAtPersistido = idsPlanos[idxPersistido];
            const persistedEsPendiente =
                idxPersistido > 0 &&
                idxPersistido < idsPlanos.length &&
                idAtPersistido != null &&
                !votosSet.has(idAtPersistido);

            const idxObjetivo = persistedEsPendiente
                ? idxPersistido
                : primerPendiente;
            dispatch({ type: "SET_INDICE", payload: idxObjetivo });
        } catch (e) {
            dispatch({
                type: "SET_ERROR",
                payload: e instanceof Error ? e.message : "error_cargar_brechas",
            });
            dispatch({ type: "SET_BRECHAS", payload: [] });
        }
    }

    // Lista plana de statements en orden lineal de presentación
    const statementsPlanas = useMemo(() => {
        /** @type {import("../lib/schemas").Statement[]} */
        const flat = [];
        for (const b of state.brechas) {
            for (const s of b.statements) flat.push(s);
        }
        return flat;
    }, [state.brechas]);

    /**
     * Dado un índice plano, devuelve la brecha a la que pertenece la statement.
     * @param {number} idx
     */
    function brechaDeStatement(idx) {
        let acc = 0;
        for (const b of state.brechas) {
            if (idx < acc + b.statements.length) return b;
            acc += b.statements.length;
        }
        return undefined;
    }

    const value = useMemo(
        () => ({ state, dispatch, statementsPlanas, brechaDeStatement, cargarBrechas }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state, statementsPlanas]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useParticipacion() {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error("useParticipacion debe usarse dentro de <ParticipacionProvider>");
    return ctx;
}

/**
 * Limpia toda la sesión (memoria + sessionStorage). Útil al completar
 * o al cancelar.
 */
export function useLimpiarSesion() {
    const { dispatch } = useParticipacion();
    return () => {
        limpiarSesion();
        dispatch({ type: "RESET" });
    };
}
